export const supportedLanguages = ['nb', 'en'] as const

export type Language = (typeof supportedLanguages)[number]

export const defaultLanguage: Language = 'en'

export const translations = {
  nb: {
    app: {
      maritimeAuthority: 'Sjøfartsdirektoratet',
      title: 'Fartøysertifikater',
    },
    breadcrumbs: {
      ariaLabel: 'Du er her:',
      back: 'Tilbake',
      vessels: 'Fartøy',
      vessel: 'Fartøy',
      backToVessels: 'Tilbake til fartøy',
      backToVessel: 'Tilbake til {{vesselName}}',
    },
    certificate: {
      detailsAriaLabel: '{{certificateType}} sertifikatdetaljer for {{vesselName}}',
      downloadHeading: 'Skriv inn UTN-nummer for å laste ned sertifikat:',
      errorLoading: 'Feil ved lasting av sertifikat: {{message}}',
      loading: 'Laster sertifikat...',
      noExpiry: 'Ingen utløpsdato',
      notFound: 'Fant ikke sertifikat',
      titleFallback: 'Sertifikat',
      type: 'Sertifikattype',
      validFrom: 'Gyldig fra',
      validTo: 'Gyldig til',
    },
    certificateDownload: {
      button: 'Last ned',
      genericError: 'Det oppstod en feil. Prøv igjen senere.',
      incorrectUtn: 'Ugyldig UTN',
      inputLabel: 'UTN-nummer',
      loading: 'Åpner...',
      missingPdfOrIncorrectUtn: 'Fant ingen PDF eller UTN-nummeret er feil.',
      missingUtn: 'Skriv inn UTN-nummer for å laste ned sertifikatet.',
      placeholder: 'Skriv inn UTN-nummer',
    },
    certificateTypes: {
      AFS: 'Internasjonalt bunnstoffsertifikat',
      AFS_ENDORSEMENT: 'Påtegning til internasjonalt bunnstoffsertifikat',
      CCC: 'Sikkerhetssertifikat for konstruksjon av lasteskip (CCC)',
      CEC: 'Sikkerhetssertifikat for utstyr på lasteskip (CEC)',
      CEC_RECORD: 'Utstyrsfortegnelse for sikkerhetssertifikat for utstyr på lasteskip (CEC)',
      CLB: 'Sertifikat om ansvarsforsikring for bunkersoljesøl (CLB)',
      CLC: 'Sertifikat om ansvarsforsikring for oljesølskade (CLC)',
      DSC: 'Sertifikat for konstruksjon og utstyr for dynamisk støttede fartøy (DSC)',
      DSCP: 'Operasjonstillatelse for dynamisk støttede fartøy (DSCP)',
      DMLC: 'Erklæring om samsvar med Maritime Labour Convention del I (DMLC)',
      'DMLC-I': 'Erklæring om samsvar med Maritime Labour Convention del I (DMLC)',
      DMLC_I: 'Erklæring om samsvar med Maritime Labour Convention del I (DMLC_I)',
      EXC: 'Dispensasjonssertifikat (SOLAS) (EXC)',
      EXC_SINGLE_VOYAGE: 'Dispensasjonssertifikat (SOLAS) - enkel reise (EXC)',
      EXCF: 'Dispensasjonssertifikat for fiske- og fangstfartøy (EXCF)',
      FISHING_TRADING: 'Fartssertifikat for fiske- og fangstfartøy',
      FISHING_TRADING_RECORD: 'Utstyrsfortegnelse for fartssertifikat for fiske- og fangstfartøy',
      FSL: 'Fartssertifikat for lasteskip og lektere (FSL)',
      FSL_WITHOUT_FREEBOARD: 'Fartssertifikat for lasteskip og lektere uten fribord (FSL)',
      HSC: 'Sikkerhetssertifikat for hurtiggående fartøy (HSC)',
      HSC_1994: 'Sikkerhetssertifikat for hurtiggående fartøy 1994 (HSC)',
      HSC_1994_RECORD: 'Utstyrsfortegnelse for HSC 1994',
      HSC_2000: 'Sikkerhetssertifikat for hurtiggående fartøy 2000 (HSC)',
      HSC_2000_RECORD: 'Utstyrsfortegnelse for HSC 2000',
      HSCP: 'Operasjonstillatelse for hurtiggående fartøy (HSCP)',
      HSCP_1994: 'Operasjonstillatelse for hurtiggående fartøy 1994 (HSCP)',
      HSCP_2000: 'Operasjonstillatelse for hurtiggående fartøy 2000 (HSCP)',
      IAPP: 'Internasjonalt sertifikat for hindring av luftforurensning (IAPP)',
      IAPP_EXEMPTION_UNMANNED_BARGE:
        'IAPP dispensasjonssertifikat for ubemannet lekter',
      IAPP_SUPPLEMENT:
        'Supplement til internasjonalt sertifikat for hindring av luftforurensning (IAPP)',
      IBWC: 'Internasjonalt sertifikat for håndtering av ballastvann (IBWC)',
      IDG: 'Sertifikat for farlig gods (IDG)',
      IDG_HSC: 'Sertifikat for farlig gods for hurtiggående fartøy (IDG)',
      IEE: 'Internasjonalt energieffektivitetssertifikat (IEE)',
      IEE_SUPPLEMENT: 'Supplement til internasjonalt energieffektivitetssertifikat (IEE)',
      IHM: 'Sertifikat for inventar over farlige materialer (IHM)',
      ILL_EXEMPTION: 'Internasjonalt fribordsdispensasjonssertifikat',
      ILL_EXEMPTION_SINGLE: 'Internasjonalt fribordsdispensasjonssertifikat - enkel reise',
      ILLCL: 'Internasjonalt fribordssertifikat (ILLCL)',
      ILLCP: 'Internasjonalt fribordssertifikat (ILLCP)',
      IOPP: 'Internasjonalt sertifikat for hindring av oljeforurensning (IOPP)',
      IOPP_EXEMPTION_UNMANNED_BARGE:
        'IOPP dispensasjonssertifikat for ubemannet lekter',
      IOPP_FORM_A: 'Supplement Form A til IOPP-sertifikat',
      IOPP_FORM_B: 'Supplement Form B til IOPP-sertifikat',
      IOPP_MOBILE_OFFSHORE: 'IOPP supplement for flyttbare innretninger',
      ISM: 'Sikkerhetsstyringssertifikat (ISM)',
      ISM_INTERIM: 'Midlertidig sikkerhetsstyringssertifikat (ISM)',
      ISPP: 'ISPP dispensasjonssertifikat for ubemannet lekter',
      ISPPC: 'Internasjonalt sertifikat for hindring av kloakkforurensning (ISPPC)',
      ISSC: 'Internasjonalt sikringssertifikat for skip (ISSC)',
      ISSC_INTERIM: 'Midlertidig internasjonalt sikringssertifikat for skip (ISSC)',
      ML: 'Fribordssertifikat (ML)',
      MLC: 'Maritime Labour Certificate (MLC)',
      MLC_INTERIM: 'Interim Maritime Labour Certificate (MLC)',
      MOU: 'Sertifikat for flyttbar innretning (MOU)',
      MOU_FITNESS: 'Sertifikat for egnethet for flyttbar innretning (MOU)',
      MOU_FITNESS_ANNEX: 'Vedlegg I til sertifikat for egnethet for flyttbar innretning (MOU)',
      MOU_FITNESS_ANNEX_JACKUP:
        'Vedlegg I til sertifikat for egnethet for oppjekkbar flyttbar innretning (MOU)',
      MOU_SAFETY_CONSTRUCTION: 'Sikkerhetssertifikat for konstruksjon for flyttbar innretning (MOU)',
      MOU_SAFETY_EQUIPMENT: 'Sikkerhetssertifikat for utstyr for flyttbar innretning (MOU)',
      NLS: 'Sertifikat for skadelige flytende stoffer (NLS)',
      OIL_RECOVERY: 'Sertifikat for fartøy i oljevernberedskap',
      PASSENGER_DOMESTIC_EC: 'Sikkerhetssertifikat for passasjerskip i innenriks fart (EF)',
      PLR: 'Sertifikat om forsikring for passasjerer og bagasje til sjøs (PLR)',
      POLAR: 'Polarskipsertifikat',
      'POLAR-L': 'Polarskipsertifikat for lasteskip (POLAR-L)',
      POLAR_L: 'Polarskipsertifikat for lasteskip (POLAR-L)',
      'POLAR-P': 'Polarskipsertifikat for passasjerskip (POLAR-P)',
      POLAR_P: 'Polarskipsertifikat for passasjerskip (POLAR-P)',
      POLAR_RECORD: 'Utstyrsfortegnelse for polarskipsertifikat',
      PS: 'Passasjersertifikat (PS)',
      PS_HERITAGE: 'Passasjersertifikat for vernet eller fredet skip',
      PS_HERITAGE_WITHOUT_FREEBOARD:
        'Passasjersertifikat for vernet eller fredet skip uten fribord',
      PS_WITHOUT_FREEBOARD: 'Passasjersertifikat uten fribord (PS)',
      PSSC: 'Sikkerhetssertifikat for passasjerskip (PSSC)',
      PSSC_ANNEX: 'Vedlegg til sikkerhetssertifikat for passasjerskip (PSSC)',
      PSSC_RECORD: 'Utstyrsfortegnelse for sikkerhetssertifikat for passasjerskip (PSSC)',
      SMAN: 'Bemanningssertifikat (SMAN)',
      SMANF: 'Bemanningssertifikat for fiske- og fangstfartøy (SMANF)',
      SMANF_ATTACHMENT: 'Vedlegg til bemanningssertifikat for fiske- og fangstfartøy (SMANF)',
      TMC: 'Målebrev (TMC)',
      TMC_ATTACHMENT: 'Vedlegg til målebrev (TMC)',
      TMC_NATIONAL: 'Norsk innenriks målebrev (TMC)',
      TRADE_PERMIT: 'Fartstillatelse',
      WFC: 'Sertifikat for arbeids- og levevilkår om bord på fiske- og fangstfartøy (ILO 188)',
      WRC: 'Vrakfjerningssertifikat (WRC)',
    },
    common: {
      camera: 'Kamera',
      close: 'Lukk',
      download: 'Last ned',
      errorLoading: 'Feil ved lasting: {{message}}',
      help: 'Hjelp',
      imoNumber: 'IMO-nummer',
      primaryMobileNavigation: 'Primær mobilnavigasjon',
      requiredField: 'Obligatorisk felt',
      remove: 'Fjern',
      scan: 'Skann',
      search: 'Søk',
      status: 'Status',
      type: 'Type',
      validity: 'Gyldighet',
      vesselName: 'Fartøynavn',
      vessels: 'Fartøy',
    },
    footer: {
      about: 'Om sdir.no',
      accessibility: 'Tilgjengelighetserklæring',
      contact: 'Kontakt oss',
      followUs: 'Følg oss',
      facebook: 'Besøk oss på Facebook',
      instagram: 'Besøk oss på Instagram',
      linkedIn: 'Besøk oss på LinkedIn',
      privacy: 'Personvernerklæring',
      tagline: '- Den foretrukne maritime administrasjonen',
    },
    header: {
      clearSearch: 'Tøm søk',
      goToVesselSearch: 'Gå til fartøysøk',
      searchHeading: 'Søk etter sertifikat',
      searchLabel: 'Fartøynavn, IMO-nummer eller sertifikat-ID',
      searchPlaceholder: 'Søk etter fartøynavn, IMO-nummer eller sertifikat-ID',
    },
    help: {
      accessibility: 'Tilgjengelighetserklæring',
      contact: 'Kontakt oss',
      followUs: 'Følg oss',
      title: 'Hjelp',
    },
    language: {
      switchToEnglish: 'Bytt språk til engelsk',
      switchToNorwegian: 'Switch language to Norwegian',
      toggleLabel: 'English',
    },
    layout: {
      externalLink: 'Lenke til et annet nettsted',
      menu: 'Meny',
      sdirLink: 'Gå til Sjøfartsdirektoratet (sdir.no)',
      selectLanguage: 'Velg språk',
      skipToContent: 'Hopp til hovedinnhold',
    },
    navigation: {
      help: 'Hjelp',
      scan: 'Skann',
      vessels: 'Fartøy',
    },
    qrcode: {
      cameraPermissionDenied: 'Kameratilgang ble avvist. Tillat kameratilgang for å skanne QR-koder.',
      cameraStartFailed: 'Kunne ikke starte kameraskanneren.',
      cameraWithNumber: 'Kamera {{number}}',
      noCamera: 'Fant ikke kamera på denne enheten.',
      noCertificateFound: 'Fant ikke sertifikat for QR-koden.',
      noCertificateId: 'Fant ingen sertifikat-ID i QR-koden.',
      openingCertificate: 'Åpner sertifikat...',
      somethingWentWrong: 'Noe gikk galt. Prøv igjen senere.',
    },
    vessel: {
      availableOfflineLabel: '{{action}} {{vesselName}} for offline bruk',
      certificatesFound: '{{count}} sertifikater funnet',
      downloadAriaLabel: 'Last ned {{certificateName}}',
      downloadDialog: 'Skriv inn UTN-nummer for å laste ned {{certificateLabel}}.',
      errorLoading: 'Feil ved lasting av fartøy: {{message}}',
      faqAriaLabel: 'Ofte stilte spørsmål',
      loading: 'Laster fartøy...',
      noCertificatesFound: 'Fant ingen sertifikater',
      noExpiry: 'Ingen utløpsdato',
      notFound: 'Fant ikke fartøy',
      offlineQuestion: 'Hva kan jeg bruke offline til?',
      offlineText:
        'Lagrede fartøy og tilgjengelig sertifikatinformasjon kan åpnes igjen uten søk. Husk å oppdatere fartøyinformasjonen når du har internettforbindelse, slik at du har siste oppdateringer.',
      remove: 'Fjern',
      save: 'Lagre',
      saveOffline: 'Lagre offline',
      savedOffline: 'Lagret offline',
      tableCaption: 'Sertifikater for {{vesselName}}',
      vesselDetails: 'Fartøydetaljer',
    },
    vessels: {
      count: '{{count}} fartøy',
      emptySavedTitle: 'Lagrede fartøy',
      errorLoading: 'Feil ved lasting av fartøy: {{message}}',
      faqAriaLabel: 'Ofte stilte spørsmål',
      loading: 'Laster fartøy...',
      noResultsFor: 'Fant ingen fartøy for {{searchTerm}}.',
      noVesselsFound: 'Fant ingen fartøy',
      offlineAriaLabel: '{{count}} offline fartøy',
      offlineCaption: 'Offline fartøy',
      offlineQuestion: 'Hva kan jeg bruke offline?',
      offlineText:
        'Lagrede fartøy og tilgjengelig sertifikatinformasjon kan åpnes igjen uten søk. Husk å oppdatere fartøyinformasjonen når du har internettforbindelse, slik at du har siste oppdateringer.',
      removeAriaLabel: 'Fjern {{vesselName}}',
      savedTitle: 'Fartøysertifikat',
      searchFailed: 'Kunne ikke søke etter fartøy. Prøv igjen senere.',
      searchForStart: 'Søk etter et fartøy for å komme i gang.',
      searchResults: 'Søkeresultater',
      searchResultsAriaLabel: '{{count}} søkeresultater',
      searchResultsCaption: 'Søkeresultater for {{searchTerm}}',
      searchingFor: 'Søker etter "{{searchTerm}}"...',
    },
  },
  en: {
    app: {
      maritimeAuthority: 'Norwegian Maritime Authority',
      title: 'Vessel Certificates',
    },
    breadcrumbs: {
      ariaLabel: 'You are here:',
      back: 'Back',
      vessels: 'Vessels',
      vessel: 'Vessel',
      backToVessels: 'Back to vessels',
      backToVessel: 'Back to {{vesselName}}',
    },
    certificate: {
      detailsAriaLabel: '{{certificateType}} certificate details for {{vesselName}}',
      downloadHeading: 'Insert UTN number to download certificate:',
      errorLoading: 'Error loading certificate: {{message}}',
      loading: 'Loading certificate...',
      noExpiry: 'No expiry',
      notFound: 'Certificate not found',
      titleFallback: 'Certificate',
      type: 'Certificate type',
      validFrom: 'Valid from',
      validTo: 'Valid to',
    },
    certificateDownload: {
      button: 'Download',
      genericError: 'An error occurred, please try again later.',
      incorrectUtn: 'Invalid UTN',
      inputLabel: 'UTN number',
      loading: 'Opening...',
      missingPdfOrIncorrectUtn: 'No PDF found or incorrect UTN number.',
      missingUtn: 'Insert UTN number to download the certificate.',
      placeholder: 'Insert UTN number',
    },
    certificateTypes: {
      AFS: 'International Anti-Fouling System Certificate',
      AFS_ENDORSEMENT: 'Endorsement to the International Anti-Fouling System Certificate',
      CCC: 'Cargo Ship Safety Construction Certificate (CCC)',
      CEC: 'Cargo Ship Safety Equipment Certificate (CEC)',
      CEC_RECORD: 'Record of Equipment for Cargo Ship Safety Equipment Certificate (CEC)',
      CLB: 'Certificate on Civil Liability for Bunker Oil Pollution Damage (CLB)',
      CLC: 'Certificate on Civil Liability for Oil Pollution Damage (CLC)',
      DSC: 'Dynamically Supported Craft Construction and Equipment Certificate (DSC)',
      DSCP: 'Permit to operate Dynamically Supported Craft (DSCP)',
      DMLC: 'Declaration of Maritime Labour Compliance Part I (DMLC)',
      'DMLC-I': 'Declaration of Maritime Labour Compliance Part I (DMLC)',
      DMLC_I: 'Declaration of Maritime Labour Compliance Part I (DMLC_I)',
      EXC: 'Exemption Certificate (SOLAS) (EXC)',
      EXC_SINGLE_VOYAGE: 'Exemption Certificate (SOLAS) - Single voyage (EXC)',
      EXCF: 'Exemption Certificate Fishing Vessel (EXCF)',
      FISHING_TRADING: 'Trading Certificate for Fishing Vessel',
      FISHING_TRADING_RECORD: 'Record of Equipment for Trading Certificate for Fishing Vessel',
      FSL: 'Cargo Ship Trading Certificate (FSL)',
      FSL_WITHOUT_FREEBOARD: 'Cargo Ship Trading Certificate without freeboard (FSL)',
      HSC: 'High-Speed Craft Safety Certificate (HSC)',
      HSC_1994: 'High-Speed Craft Safety Certificate 1994 (HSC)',
      HSC_1994_RECORD: 'Record of Equipment for HSC 1994',
      HSC_2000: 'High-Speed Craft Safety Certificate 2000 (HSC)',
      HSC_2000_RECORD: 'Record of Equipment for HSC 2000',
      HSCP: 'Permit to Operate High Speed Craft (HSCP)',
      HSCP_1994: 'Permit to Operate High Speed Craft 1994 (HSCP)',
      HSCP_2000: 'Permit to Operate High Speed Craft 2000 (HSCP)',
      IAPP: 'International Air Pollution Prevention Certificate (IAPP)',
      IAPP_EXEMPTION_UNMANNED_BARGE:
        'IAPP Exemption Certificate for unmanned barge',
      IAPP_SUPPLEMENT: 'Supplement to the International Air Pollution Prevention Certificate (IAPP)',
      IBWC: 'International Ballast Water Management Certificate (IBWC)',
      IDG: 'Dangerous Goods Certificate (IDG)',
      IDG_HSC: 'Dangerous Goods Certificate for High Speed Craft (IDG)',
      IEE: 'International Energy Efficiency Certificate (IEE)',
      IEE_SUPPLEMENT: 'Supplement to the International Energy Efficiency Certificate (IEE)',
      IHM: 'Certificate of Inventory of Hazardous Materials (IHM)',
      ILL_EXEMPTION: 'International Load Line Exemption Certificate',
      ILL_EXEMPTION_SINGLE: 'International Load Line Exemption Certificate - Single voyage',
      ILLCL: 'International Load Line Certificate (ILLCL)',
      ILLCP: 'International Load Line Certificate (ILLCP)',
      IOPP: 'International Oil Pollution Prevention Certificate (IOPP)',
      IOPP_EXEMPTION_UNMANNED_BARGE:
        'IOPP Exemption Certificate for unmanned barge',
      IOPP_FORM_A: 'Supplement Form A to the IOPP Certificate',
      IOPP_FORM_B: 'Supplement Form B to the IOPP Certificate',
      IOPP_MOBILE_OFFSHORE: 'IOPP Supplement for mobile offshore units',
      ISM: 'Safety Management Certificate (ISM)',
      ISM_INTERIM: 'Interim Safety Management Certificate (ISM)',
      ISPP: 'ISPP Exemption Certificate for unmanned barge',
      ISPPC: 'International Sewage Pollution Prevention Certificate (ISPPC)',
      ISSC: 'International Ship Security Certificate (ISSC)',
      ISSC_INTERIM: 'Interim International Ship Security Certificate (ISSC)',
      ML: 'Load Line Certificate (ML)',
      MLC: 'Maritime Labour Certificate (MLC)',
      MLC_INTERIM: 'Interim Maritime Labour Certificate (MLC)',
      MOU: 'Mobile Offshore Unit Certificate (MOU)',
      MOU_FITNESS: 'MOU Certificate of Fitness',
      MOU_FITNESS_ANNEX: 'Annex I to MOU Certificate of Fitness',
      MOU_FITNESS_ANNEX_JACKUP: 'Annex I to MOU Certificate of Fitness - jackup',
      MOU_SAFETY_CONSTRUCTION: 'MOU Safety Construction Certificate',
      MOU_SAFETY_EQUIPMENT: 'MOU Safety Equipment Certificate',
      NLS: 'Noxious Liquid Substances Certificate (NLS)',
      OIL_RECOVERY: 'Certificate for vessel in oil spill response preparedness',
      PASSENGER_DOMESTIC_EC: 'Passenger Ship Safety Certificate in domestic trade (EC)',
      PLR: 'Certificate of Insurance for Passengers and Luggage at Sea (PLR)',
      POLAR: 'Polar Ship Certificate',
      'POLAR-L': 'Polar Ship Certificate for Cargo Ship (POLAR-L)',
      POLAR_L: 'Polar Ship Certificate for Cargo Ship (POLAR-L)',
      'POLAR-P': 'Polar Ship Certificate for Passenger Ship (POLAR-P)',
      POLAR_P: 'Polar Ship Certificate for Passenger Ship (POLAR-P)',
      POLAR_RECORD: 'Record of Equipment for the Polar Ship Certificate',
      PS: 'Passenger Certificate (PS)',
      PS_HERITAGE: 'Passenger Certificate for protected or listed ship',
      PS_HERITAGE_WITHOUT_FREEBOARD:
        'Passenger Certificate for protected or listed ship without freeboard',
      PS_WITHOUT_FREEBOARD: 'Passenger Certificate without freeboard (PS)',
      PSSC: 'Passenger Ship Safety Certificate (PSSC)',
      PSSC_ANNEX: 'Annex to Passenger Ship Safety Certificate (PSSC)',
      PSSC_RECORD: 'Record of Equipment for Passenger Ship Safety Certificate (PSSC)',
      SMAN: 'Safe Manning Certificate (SMAN)',
      SMANF: 'Safe Manning Certificate for Fishing Vessel (SMANF)',
      SMANF_ATTACHMENT: 'Safe Manning Certificate attachment for Fishing Vessel (SMANF)',
      TMC: 'Tonnage Measurement Certificate (TMC)',
      TMC_ATTACHMENT: 'Tonnage Measurement Certificate attachment (TMC)',
      TMC_NATIONAL: 'Norwegian domestic tonnage certificate (TMC)',
      TRADE_PERMIT: 'Trade Permit',
      WFC: 'Certificate for working and living conditions on board fishing vessels (ILO 188)',
      WRC: 'Wreck Removal Certificate (WRC)',
    },
    common: {
      camera: 'Camera',
      close: 'Close',
      download: 'Download',
      errorLoading: 'Error loading: {{message}}',
      help: 'Help',
      imoNumber: 'IMO-number',
      primaryMobileNavigation: 'Primary mobile navigation',
      requiredField: 'Required field',
      remove: 'Remove',
      scan: 'Scan',
      search: 'Search',
      status: 'Status',
      type: 'Type',
      validity: 'Validity',
      vesselName: 'Vessel name',
      vessels: 'Vessels',
    },
    footer: {
      about: 'About sdir.no',
      accessibility: 'Accessibility statement',
      contact: 'Contact us',
      followUs: 'Follow us',
      facebook: 'Visit us on Facebook',
      instagram: 'Visit us on Instagram',
      linkedIn: 'Visit us on LinkedIn',
      privacy: 'Privacy statement',
      tagline: '- The preferred maritime administration',
    },
    header: {
      clearSearch: 'Clear search',
      goToVesselSearch: 'Go to vessel search',
      searchHeading: 'Search for certificate',
      searchLabel: 'Vessel name, IMO number or certificate-ID',
      searchPlaceholder: 'Search for vessel name, IMO number or certificate-ID',
    },
    help: {
      accessibility: 'Accessibility statement',
      contact: 'Contact us',
      followUs: 'Follow us',
      title: 'Help',
    },
    language: {
      switchToEnglish: 'Bytt språk til engelsk',
      switchToNorwegian: 'Switch language to Norwegian',
      toggleLabel: 'Norsk',
    },
    layout: {
      externalLink: 'Link to another website',
      menu: 'Menu',
      sdirLink: 'Go to Sjøfartsdirektoratet (sdir.no)',
      selectLanguage: 'Select language',
      skipToContent: 'Skip to main content',
    },
    navigation: {
      help: 'Help',
      scan: 'Scan',
      vessels: 'Vessels',
    },
    qrcode: {
      cameraPermissionDenied: 'Camera permission was denied. Allow camera access to scan QR codes.',
      cameraStartFailed: 'Could not start the camera scanner.',
      cameraWithNumber: 'Camera {{number}}',
      noCamera: 'No camera was found on this device.',
      noCertificateFound: 'No certificate found for QR code.',
      noCertificateId: 'No certificate ID found in the QR code.',
      openingCertificate: 'Opening certificate...',
      somethingWentWrong: 'Something went wrong, please try again later.',
    },
    vessel: {
      availableOfflineLabel: '{{action}} {{vesselName}} for offline use',
      certificatesFound: '{{count}} certificates found',
      downloadAriaLabel: 'Download {{certificateName}}',
      downloadDialog: 'Insert UTN number to download {{certificateLabel}}.',
      errorLoading: 'Error loading vessel: {{message}}',
      faqAriaLabel: 'Frequently asked questions',
      loading: 'Loading vessel...',
      noCertificatesFound: 'No certificates found',
      noExpiry: 'No expiry',
      notFound: 'Vessel not found',
      offlineQuestion: 'What can I use offline for?',
      offlineText:
        'Saved vessels and their available certificate information can be opened again without searching. Be mindful to refresh the vessel information when you have an internet connection to ensure you have the latest updates.',
      remove: 'Remove',
      save: 'Save',
      saveOffline: 'Save offline',
      savedOffline: 'Saved offline',
      tableCaption: 'Certificates for {{vesselName}}',
      vesselDetails: 'Vessel details',
    },
    vessels: {
      count: '{{count}} vessels',
      emptySavedTitle: 'Saved vessels',
      errorLoading: 'Error loading vessels: {{message}}',
      faqAriaLabel: 'Frequently asked questions',
      loading: 'Loading vessels...',
      noResultsFor: 'No vessel found for {{searchTerm}}.',
      noVesselsFound: 'No vessels found',
      offlineAriaLabel: '{{count}} offline vessels',
      offlineCaption: 'Offline vessels',
      offlineQuestion: 'What can I use offline?',
      offlineText:
        'Saved vessels and their available certificate information can be opened again without searching. Be mindful to refresh the vessel information when you have an internet connection to ensure you have the latest updates.',
      removeAriaLabel: 'Remove {{vesselName}}',
      savedTitle: 'Vessel Certificate',
      searchFailed: 'Could not search for vessels. Try again later.',
      searchForStart: 'Search for a vessel to get started.',
      searchResults: 'Search results',
      searchResultsAriaLabel: '{{count}} search results',
      searchResultsCaption: 'Search results for {{searchTerm}}',
      searchingFor: 'Searching for "{{searchTerm}}"...',
    },
  },
} as const
